/**
 * Copy for the Meta Advanced row (APP-548) on Setup → Connectors. The API owns the
 * rules (`meta-brand-credential.service.ts`); this file only owns the words.
 */

export const META_ADVANCED_TITLE = "Meta Advanced"

/** The row is shown to super admins only; brand users never see it. */
export const META_ADVANCED_BADGE = "Super admin"

export const META_ADVANCED_HELP =
  "Marathon-managed system user. Once you move the brand's Meta edits here, its ad changes run on it. It also stands in for the Instagram Insights login when that login fails."

export const META_ADVANCED_EDITS_ON_CONFIRM =
  "Move this brand's Meta edits to Meta Advanced? Campaign, ad set and ad changes, blasts and creative tests will run on the system user, and the Instagram Insights login will stop making changes. Profile-Visit ads and partnership ads may fail: the MarathonData Meta app does not offer system users the permissions they need (pages_manage_ads, instagram_branded_content_ads_brand). A blast or ad creation that already runs fails at its next step; replay it from Jobs to finish it on Meta Advanced."

export const META_ADVANCED_EDITS_OFF_CONFIRM =
  "Move this brand's Meta edits back to the Instagram Insights login? A blast or ad creation that already runs fails at its next step; replay it to finish it on the login."

export const META_ADVANCED_REMOVE_CONFIRM =
  "Remove the Meta Advanced credential? Marathon deletes its copy of the token. If Instagram data runs on this credential now, the Instagram Insights connector loses its token and asks the brand to reconnect. Meta keeps the token valid until you revoke it in Business settings."

// APP-566 T3: with the tier order on (`status.tierOrder`), Pause replaces the
// edit switch.

export const META_ADVANCED_HELP_TIER_ORDER =
  "Marathon-managed system user. Meta reads and edits for this brand run on it first; the brand's Instagram Insights login takes over while it is paused or not usable. Partnership launches, the creator-permissions read and comment moderation use it only when its token has their permissions. It also stands in for the Instagram Insights login when that login fails."

export const META_ADVANCED_IN_USE = "Meta edits and reads use this system user"

export const META_ADVANCED_PAUSED =
  "Paused: edits and reads use the brand's login, if it is connected"

/** Followed by a space and the API's `systemUserSkip.detail`. */
export const META_ADVANCED_NOT_USED =
  "Not used: edits and reads use the brand's login, if it is connected."

export const metaAdvancedInvalidLine = (day: string) =>
  `Meta refused this system user's token on ${day}. Until you replace it, edits and reads use the brand's login, if it is connected.`

export const META_ADVANCED_PAUSE_CONFIRM =
  "Pause this system user? Meta reads and edits for this brand move to its Instagram Insights login, if it is connected. A blast or ad creation that already runs fails at its next step; replay it to finish it on the login. The Instagram Insights data fallback is not affected."

export const META_ADVANCED_RESUME_CONFIRM =
  "Resume this system user? Meta reads and edits for this brand move back to it. A blast or ad creation that already runs fails at its next step; replay it to finish it on the system user."

export const META_ADVANCED_REMOVE_CONFIRM_TIER_ORDER =
  "Remove the Meta Advanced credential? Marathon deletes its copy of the token, and Meta reads and edits for this brand move to its Instagram Insights login, if it is connected. If Instagram data runs on this credential now, the Instagram Insights connector loses its token and asks the brand to reconnect. Meta keeps the token valid until you revoke it in Business settings."

export const META_ADVANCED_PAUSED_TOAST =
  "Meta Advanced paused: edits and reads use the brand's login, if it is connected."
export const META_ADVANCED_RESUMED_TOAST = "Meta Advanced resumed."
export const META_ADVANCED_PAUSE_ERROR =
  "Couldn't pause Meta Advanced. Try again."
export const META_ADVANCED_RESUME_ERROR =
  "Couldn't resume Meta Advanced. Try again."

// APP-566 T5 (D25): the status's `accountUserNext` says the company's account
// user, not the brand's login, comes next after the system user.

const NEXT_CREDENTIAL_PHRASES = [
  /the brand's login, if it is connected/g,
  /its Instagram Insights login, if it is connected/g,
  /the brand's Instagram Insights login/g,
]
const ACCOUNT_USER_OR_LOGIN =
  "the company's account user (Settings → Company → Meta access) or the brand's login"

/**
 * A tier-order sentence that names the next credential
 * (META_ADVANCED_HELP_TIER_ORDER, META_ADVANCED_PAUSED, META_ADVANCED_NOT_USED,
 * metaAdvancedInvalidLine, META_ADVANCED_PAUSE_CONFIRM,
 * META_ADVANCED_REMOVE_CONFIRM_TIER_ORDER, META_ADVANCED_PAUSED_TOAST): as it
 * is when `accountUserNext` is false, else with the account user named before
 * the brand's login.
 */
export function withNextCredential(
  text: string,
  accountUserNext: boolean,
): string {
  if (!accountUserNext) return text
  return NEXT_CREDENTIAL_PHRASES.reduce(
    (sentence, phrase) => sentence.replace(phrase, ACCOUNT_USER_OR_LOGIN),
    text,
  )
}

/** Mirrors the API DTO's `@MinLength(32)`. */
export const MIN_META_ADVANCED_TOKEN_LENGTH = 32

export const META_ADVANCED_TOKEN_LABEL = "System user access token"

export const META_ADVANCED_TOKEN_PLACEHOLDER =
  "Paste the token generated for the MarathonData app"

export const META_ADVANCED_TOKEN_NOTE =
  "Marathon checks the token with Meta before saving it, stores it encrypted and never shows it again. Saving also checks the Instagram Insights connector: if its login is broken, its data moves to this token at once."

/** Every permission Meta offers a system user on the MarathonData app; the API requires all of them. */
export const META_ADVANCED_PERMISSIONS = [
  "ads_management",
  "ads_read",
  "business_management",
  "instagram_basic",
  "instagram_manage_comments",
  "instagram_manage_insights",
  "pages_read_engagement",
  "pages_show_list",
]

/** Inline: the runbook lives in a private repo. */
export const META_ADVANCED_SETUP_STEPS = [
  "Create or pick a system user for this client in Marathon's Business Manager.",
  "Assign it this brand's ad account, Page and Instagram account.",
  `Generate a never-expiring token for the MarathonData app with all ${META_ADVANCED_PERMISSIONS.length} permissions offered: ${META_ADVANCED_PERMISSIONS.join(", ")}.`,
]
